import React, { useEffect, useState } from 'react';

import {
	Drawer, OverlayContent, OverlayDisclosure, useOverlayState
} from 'components/Overlay';
import { setGenericEvent } from 'features/tealium/ga4/custom';
import { ProductSortItem, ProductItemSkeleton } from 'features/product/components';
import { getLightingCollections } from 'features/product/api';
import { SkeletonText } from 'components/Skeleton';
import decodeHtml from 'utilities/decodeHtml';

import './AppLightingCollectionsSlideOut.scss';

type LightingCollectionSlideOutProps = {
	shortSku: string;
	lightingCollectionName: string;
}

const cachedData = new Map();

function AppLightingCollectionsSlideOut ({ shortSku, lightingCollectionName }: LightingCollectionSlideOutProps) {

	const [ products, setProducts ] = useState<LP.product.LightingCollectionResponse>();
	const [ loaded, setLoaded ] = useState(() => cachedData.has('lightingCollectionData'));
	const decodedLightingCollectionName = decodeHtml(lightingCollectionName);
	const drawer = useOverlayState();

	const productSortItemDisplayOptions: LP.sort.ProductSortItemDisplayOptions = {
		showAddToCart: false,
		showMoreLikeThis: false,
		showSaleCallout: false,
		showSavePrice: false,
		showShippingCallout: false,
		showJustForYouSaleCallout: false,
		showProductReviewRating: true
	};

	const onButtonClick = () => {

		setGenericEvent({
			event_name: 'pdp_collection_open',
			item_collection_name: decodedLightingCollectionName,
			pdp_item_id: shortSku
		});

		(async () => {

			if (cachedData.has('lightingCollectionData')) {

				setProducts(cachedData.get('lightingCollectionData'));
				return;

			}

			const data = await getLightingCollections(shortSku);
			cachedData.set('lightingCollectionData', data);
			setProducts(data);
			setLoaded(true);

		})();

		drawer.showOverlay();

	};

	const onViewFullCollectionClick = () => {

		setGenericEvent({
			event_name: 'pdp_collection_view_full_click',
			item_collection_name: decodedLightingCollectionName,
			pdp_item_id: shortSku
		});

	};

	const onClickProduct = (e: React.MouseEvent, product: LP.product.ProductItem) => {

		setGenericEvent({
			event_name: 'pdp_collection_item_click',
			item_collection_name: products.CollectionName,
			pdp_item_id: shortSku,
			item_position_number: product.SortPosition
		});

	};

	useEffect(() => {

		setGenericEvent({
			event_name: 'pdp_collection_load',
			item_collection_name: decodedLightingCollectionName,
			pdp_item_id: shortSku
		});

	}, []);

	const skeletonCount = 8;
	const a = Array(skeletonCount).fill(1);
	// eslint-disable-next-line react/no-array-index-key
	const skeletons = a.map((i, key) => <ProductItemSkeleton key={key} />);

	return (
		<>
			<p>
				<OverlayDisclosure {...drawer} onButtonClick={onButtonClick} buttonStyleVariant="link">
					{decodedLightingCollectionName}
					{' '}
					Collection
				</OverlayDisclosure>
			</p>
			<Drawer
				{...drawer}
				aria-label="Lighting Collections Slide Out"
				escapeExits
				className="AppLightingCollectionsSlideOut"
				anchorEdge="right"
				showCloseButton={false}
			>
				<OverlayContent>
					<div className="appLightingCollectionsSlideOutWrapper" data-capture-section="LightingCollectionsSlideOutPDP">
						<div className="AppLightingCollectionsSlideOut__closeButtonWrapper">
							<button
								onClick={() => drawer.hideOverlay()}
								className="AppLightingCollectionsSlideOut__closeButton"
								aria-label="Close"
								type="button"
							>
								<span className="AppLightingCollectionsSlideOut__closeButtonText">Close</span>
								<span className="lpIcon-close02" />
							</button>
						</div>
						<header className="lightingCollectionsSlideOutHeader">
							{loaded && (
								<div className="lightingCollectionsSlideOutHeader__container">
									<div className="lightingCollectionsSlideOutHeader__title">
										{decodeHtml(products.CollectionName)}
									</div>
									{/* <a
										href={products.LightingCollectionUrl}
										className="lightingCollectionsSlideOutHeader__link"
										onClick={onViewFullCollectionClick}
									>
											View Full Collection
									</a> */}
								</div>
							)}
							{!loaded && (
								<div className="lightingCollectionsSlideOutHeader__container">
									<SkeletonText lines={1} />
									<SkeletonText header width="120px" />
								</div>
							)}
						</header>
						<main className="lightingCollectionsSlideOutMain">
							{loaded && (
								<div className="lightingCollectionsSlideOutMain__layout">
									{products.Products.map((product) => (
										<ProductSortItem
											product={product}
											key={product.ShortSku}
											displayOptions={productSortItemDisplayOptions}
											handleClickProduct={(e: React.MouseEvent) => onClickProduct(e, product)}
										/>
									))}
								</div>
							)}
							{!loaded && (
								<div className="lightingCollectionsSlideOutMain__layout">
									{skeletons}
								</div>
							)}

						</main>
					</div>

				</OverlayContent>
			</Drawer>
		</>
	);

}

export default AppLightingCollectionsSlideOut;
